import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import type { RootState } from '../../../../configuration/setup/store';
import { AssetsListDetailTab } from '../../containers/assets/details/types';
import type { KnownDialogTitleKeys } from '../../components/assets/easyOnboarding/types.ts';
import type { AssetsState } from './types';

export const initialState: AssetsState = {
    selectedAssetId: null,
    tableViewType: TableViewToggles.VIEW_TYPE_TABLE,
    displayDeleteDialog: false,
    searchString: '',
    filters: {
        showAssetsWithDataSource: true,
        showAssetsWithoutDataSource: true,
        showActiveAssets: true,
        showArchivedAssets: true,
    },
    tableDisplayOptions: { gridLines: false, compactRows: false },
    showFilterOptionsDialog: false,
    sortBy: null,
    sortDir: SortDirection.ASCENDING,
    easyOnboardingDialog: { show: false, selectedAssetId: undefined },
    isAssetsReloadImminent: false,
    closeDialogTriggersReload: false,
    connectorDialogTitleOverwrite: null,
    currentAssetDetailsTab: AssetsListDetailTab.ASSET_DETAILS,
};

const assetsSlice = createSlice({
    name: 'assets',
    initialState,
    reducers: {
        assetTableViewTypeChanges: (state, action: PayloadAction<TableViewTogglesViewType>) => {
            state.tableViewType = action.payload;
        },
        setSelectedAssetId: (state, action: PayloadAction<string | null>) => {
            state.selectedAssetId = action.payload;
        },
        assetTableSortChanges: (
            state,
            action: PayloadAction<{ sortBy: string; sortDir: SortDirectionType }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortDir = action.payload.sortDir;
        },
        assetTableSearchChanged: (state, action: PayloadAction<string>) => {
            state.searchString = action.payload;
        },
        assetTableApplyFilterChanges: (
            state,
            action: PayloadAction<{
                showAssetsWithDataSource: boolean;
                showAssetsWithoutDataSource: boolean;
                showActiveAssets: boolean;
                showArchivedAssets: boolean;
            }>
        ) => {
            state.filters = action.payload;
        },
        assetTableDisplayOptionsChanged: (
            state,
            action: PayloadAction<{ gridLines?: boolean; compactRows?: boolean }>
        ) => {
            state.tableDisplayOptions = {
                gridLines: action.payload.gridLines ?? state.tableDisplayOptions.gridLines,
                compactRows: action.payload.compactRows ?? state.tableDisplayOptions.compactRows,
            };
        },
        assetDeleteDialogOpen: state => {
            state.displayDeleteDialog = true;
        },
        assetDeleteDialogClose: state => {
            state.displayDeleteDialog = false;
        },
        closeEasyOnboardingDialog: state => {
            state.easyOnboardingDialog = { show: false, selectedAssetId: undefined };
        },
        clearConnectorDialogTitleOverwrite: state => {
            state.connectorDialogTitleOverwrite = null;
        },
        setConnectorDialogTitleOverwrite: (state, action: PayloadAction<KnownDialogTitleKeys>) => {
            state.connectorDialogTitleOverwrite = action.payload;
        },
        openEasyOnboardingDialog: (state, action: PayloadAction<string | undefined>) => {
            state.easyOnboardingDialog = { show: true, selectedAssetId: action.payload };
        },
        setIsAssetReloadImminent: (state, action: PayloadAction<boolean>) => {
            state.isAssetsReloadImminent = action.payload;
        },
        enableCloseDialogTriggersReload: state => {
            state.closeDialogTriggersReload = true;
        },
        changeAssetDetailsTab: (state, action: PayloadAction<AssetsListDetailTab>) => {
            state.currentAssetDetailsTab = action.payload;
        },
        filterOptionsDialogClose: state => {
            state.showFilterOptionsDialog = false;
        },
        filterOptionsDialogOpen: state => {
            state.showFilterOptionsDialog = true;
        },
    },
});

export const {
    assetTableViewTypeChanges,
    setSelectedAssetId,
    assetTableSortChanges,
    assetTableSearchChanged,
    assetTableApplyFilterChanges,
    assetTableDisplayOptionsChanged,
    assetDeleteDialogOpen,
    assetDeleteDialogClose,
    closeEasyOnboardingDialog,
    clearConnectorDialogTitleOverwrite,
    setConnectorDialogTitleOverwrite,
    openEasyOnboardingDialog,
    setIsAssetReloadImminent,
    enableCloseDialogTriggersReload,
    changeAssetDetailsTab,
    filterOptionsDialogClose,
    filterOptionsDialogOpen,
} = assetsSlice.actions;

export default assetsSlice.reducer;

export const getAssetsTableViewType = (state: RootState) => state.assets.tableViewType;
export const getSelectedAssetId = (state: RootState) => state.assets.selectedAssetId;
export const getAssetTableSortColumn = (state: RootState) => state.assets.sortBy;
export const getAssetTableSortDir = (state: RootState) => state.assets.sortDir;
export const getDisplayDeleteDialog = (state: RootState) => state.assets.displayDeleteDialog;
export const getAssetDetailTab = (state: RootState) => state.assets.currentAssetDetailsTab;
export const getSearchString = (state: RootState) => state.assets.searchString;
export const getShowAssetsWithDataSource = (state: RootState) => state.assets.filters.showAssetsWithDataSource;
export const getShowAssetsWithoutDataSource = (state: RootState) => state.assets.filters.showAssetsWithoutDataSource;
export const getShowActiveAssets = (state: RootState) => state.assets.filters.showActiveAssets;
export const getShowArchivedAssets = (state: RootState) => state.assets.filters.showArchivedAssets;
export const getShowFilterOptionsDialog = (state: RootState) => state.assets.showFilterOptionsDialog;
export const getTableGridLines = (state: RootState) => state.assets.tableDisplayOptions.gridLines;
export const getTableCompactRows = (state: RootState) => state.assets.tableDisplayOptions.compactRows;
export const getShowEasyOnboardingDialog = (state: RootState) => state.assets.easyOnboardingDialog.show;
export const getEasyOnboardingSelectedAssetId = (state: RootState) => state.assets.easyOnboardingDialog.selectedAssetId;
export const getIsAssetsReloadImminent = (state: RootState) => state.assets.isAssetsReloadImminent;
export const getCloseDialogTriggersReload = (state: RootState) => state.assets.closeDialogTriggersReload;
export const getConnectorDialogTitleOverwrite = (state: RootState) => state.assets.connectorDialogTitleOverwrite;
